/**
 * FEAT-033 — Editorial gate: operator selects 5 Deep Dive candidates from 8.
 *
 * Rendered in PieceView when:
 *   piece.content_type === "newsletter" && piece.ui_state === "awaiting_review"
 *
 * (Not "awaiting_approval" — that's the unrelated final publish gate. c15540e
 * 2026-07-05 fixed this component's read side but newsletter-ccm-ingestion.ts's
 * write side wasn't updated to match until 2026-07-12 — see that file's comment.)
 *
 * On submit:
 *   1. PATCH /pieces/:id { status: "approved", editorial_direction: JSON }
 *   2. POST  /pieces/:id/run
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, useApi } from "@/lib/api";
import { deriveUiState } from "@shared/ui-state";
import { useRole } from "@/lib/useRole";
import type { GenerationPhase } from "@/types/piece";

interface DeepDiveCandidate {
  rank: number;
  title: string;
  url: string;
  source_name: string;
  relevance_reason: string;
  takeaways: string[];
  content_excerpt: string;
}

interface Props {
  pieceId: number;
  generationPhases: GenerationPhase;
  candidates: DeepDiveCandidate[];
}

const REQUIRED = 5;

export function NewsletterDeepDiveSelector({ pieceId, generationPhases, candidates }: Props) {
  const api = useApi();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  // FEAT-108 / S-438 — picking the Deep Dives is an Editor's or Admin's call
  // (it PATCHes the piece and starts a run). A Reviewer sees the candidates
  // to read, with no checkboxes and no Approve button.
  const { canEdit } = useRole();

  const submit = useMutation({
    mutationFn: async () => {
      const selections = Array.from(selected).sort((a, b) => a - b);
      const uiState = deriveUiState("approved", generationPhases);
      await api(`/pieces/${pieceId}`, {
        method: "PATCH",
        body: JSON.stringify({
          status: "approved",
          editorial_direction: JSON.stringify({ deepDiveSelections: selections }),
          ui_state: uiState,
        }),
      });
      await api(`/pieces/${pieceId}/run`, { method: "POST" });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["piece", pieceId] });
      void qc.invalidateQueries({ queryKey: ["pieces"] });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    },
  });

  function toggle(rank: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rank)) {
        next.delete(rank);
      } else if (next.size < REQUIRED) {
        next.add(rank);
      }
      return next;
    });
  }

  const count = selected.size;
  const ready = count === REQUIRED && !submit.isPending;

  return (
    <section className="nl-selector">
      <header className="nl-selector-head">
        <h2 className="nl-selector-title">
          {canEdit ? "Select Deep Dive candidates" : "Deep Dive candidates"}
        </h2>
        {canEdit ? (
          <p className="nl-selector-sub">
            Choose exactly {REQUIRED} articles from the {candidates.length} curated candidates below.
            The pipeline will generate a full Deep Dive for each selected article and schedule them
            Monday–Friday next week.
          </p>
        ) : (
          <p className="nl-selector-sub">
            An Editor or Admin picks {REQUIRED} of these {candidates.length} for next week&rsquo;s
            Deep Dives.
          </p>
        )}
        {canEdit && (
          <div className="nl-selector-counter" data-ready={count === REQUIRED}>
            {count} / {REQUIRED} selected
          </div>
        )}
      </header>

      <ol className="nl-candidate-list">
        {candidates.map((c) => {
          const checked = selected.has(c.rank);
          const disabled = !canEdit || (!checked && count >= REQUIRED);
          return (
            <li
              key={c.rank}
              className={`nl-candidate${checked ? " nl-candidate--selected" : ""}${canEdit && disabled ? " nl-candidate--disabled" : ""}`}
              onClick={() => !disabled && toggle(c.rank)}
            >
              <label className="nl-candidate-label">
                {canEdit && (
                  <input
                    type="checkbox"
                    className="nl-candidate-check"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggle(c.rank)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <span className="nl-candidate-rank">#{c.rank}</span>
              </label>
              <div className="nl-candidate-body">
                <div className="nl-candidate-title-row">
                  <a
                    href={c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nl-candidate-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {c.title}
                  </a>
                  <span className="nl-candidate-source">{c.source_name}</span>
                </div>
                <p className="nl-candidate-reason">{c.relevance_reason}</p>
                {c.takeaways.length > 0 && (
                  <ul className="nl-candidate-takeaways">
                    {c.takeaways.slice(0, 3).map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {error && (
        <div className="login-error" style={{ margin: "8px 0" }}>
          {error}
        </div>
      )}

      {canEdit && (
        <footer className="nl-selector-foot">
          <button
            className="btn-dec btn-approve"
            disabled={!ready}
            onClick={() => submit.mutate()}
            style={{ minWidth: 260 }}
          >
            {submit.isPending
              ? "Generating Deep Dives…"
              : `Approve & Generate ${REQUIRED} Deep Dives`}
          </button>
          {!ready && count < REQUIRED && (
            <span className="decision-bar-hint decision-bar-hint-muted">
              Select {REQUIRED - count} more article{REQUIRED - count !== 1 ? "s" : ""} to continue
            </span>
          )}
        </footer>
      )}
    </section>
  );
}
